/* eslint-disable unicorn/no-array-callback-reference */
import { ButtonGroup } from '@components/ButtonGroup';
import {
  border,
  buildDataTestId,
  buildDataTestIdAttribute,
  buildFullDataTestId,
  DATA_TEST_ID_SELECTION,
  scrollBarMD,
} from '@src/utils';
import { useEffect, useState } from 'react';
import { ToggleTextButton } from '@components/ToggleTextButton';
import { DiagnosesTab } from './DiagnosesTab';
import { DiagnosesGroupsTab } from './DiagnosesGroupsTab';
import { useFormContext } from 'react-hook-form';
import { ReactComponent as CrossIcon } from '@icons/cross.svg';
import { DiagnosesSearchValueType, IDiagnosesGroupItem, SelectedDiagnoses } from '@src/api';
import { PropsDiagnosesContext } from './DiagnosesInput.components';
import { Dialog, Divider, IconButton, Stack, Typography } from '@mui/material';

const DIAGNOSES = 'DIAGNOSES';
const DIAGNOSIS_GROUPS = 'DIAGNOSIS GROUPS MANAGEMENT';

const returnFormatted = function ({ name, description, id }) {
  return { name, description, id };
};
const returnFormattedGroups = function ({ name, id }) {
  return { name, id };
};

export const DiagnosesModal = ({ open, setOpen, onAdd, prefix }) => {
  const { getValues, setValue, watch } = useFormContext();

  const dataTestId = buildFullDataTestId(DATA_TEST_ID_SELECTION, 'modal-add-diagnoses');

  const scaleType = getValues('scaleType') || DiagnosesSearchValueType.ICD10;

  const [diagnosesEdit = [], diagnosesGroupsEdit = []] = watch([`${prefix}.diagnoses`, `${prefix}.diagnosesGroups`]);

  const [diagnosisIdsArr = [], diagnosisGroupIdsArr = []] = watch([
    `${prefix}.diagnosisIds`,
    `${prefix}.diagnosisGroupIds`,
  ]);

  const handleClickScaleType = (value: DiagnosesSearchValueType | null) => {
    setValue('scaleType', value);
  };
  const [diagnosesInRule, setDiagnosesInRule] = useState<SelectedDiagnoses[]>([]);
  const [diagnosesGroupsInRule, setDiagnosesGroupIsRule] = useState<IDiagnosesGroupItem[]>([]);
  const [activeButton, setActiveButton] = useState<string>(DIAGNOSES);

  const addDiagnoses = (diagnoses: SelectedDiagnoses[]) => {
    setDiagnosesInRule(diagnoses);
  };

  const addDiagnosesGroups = (diagnosesGroups: IDiagnosesGroupItem[]) => {
    setDiagnosesGroupIsRule(diagnosesGroups);
  };

  const onDelete = (id: string) => {
    setDiagnosesInRule(prev => prev.filter(di => di.id !== id));
  };

  const onDeleteDiagnosesGroups = (id: number) => {
    setDiagnosesGroupIsRule(prev => prev.filter(group => group.id !== id));
  };

  const onClose = () => {
    setOpen(false);
    setActiveButton(DIAGNOSES);
  };

  const onApply = () => {
    const diagnosisIds = diagnosesInRule.map(returnFormatted);

    const diagnosisGroupIds = diagnosesGroupsInRule.map(returnFormattedGroups);
    onAdd(diagnosisIds, diagnosisGroupIds);
  };

  const deps = [JSON.stringify(diagnosesEdit), JSON.stringify(diagnosisIdsArr)];

  useEffect(() => {
    if (diagnosesEdit || diagnosisIdsArr) {
      setDiagnosesInRule([...(diagnosesEdit as SelectedDiagnoses[])]);
    }
    if (diagnosesGroupsEdit || diagnosisGroupIdsArr) {
      setDiagnosesGroupIsRule([...(diagnosesGroupsEdit as IDiagnosesGroupItem[])]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return (
    <PropsDiagnosesContext.Provider
      value={{
        addDiagnoses,
        addDiagnosesGroups,
        diagnosesInRule,
        diagnosesGroupsInRule,
        onDelete,
        onDeleteDiagnosesGroups,
        scaleType,
        setDiagnosesInRule,
        prefix,
      }}
    >
      <Dialog
        open={open}
        onClose={onClose}
        sx={{
          backgroundColor: 'rgba(55, 65, 81, 0.5)',
          '& .MuiDialog-container': { overflowY: 'scroll', alignItems: 'start', ...scrollBarMD },
        }}
        disablePortal
        hideBackdrop
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: '960px',
            maxHeight: 'none',
            minHeight: '660px',
            margin: '96px',
            boxShadow: '0px 3px 10px rgba(0, 0, 0, 0.15)',
            overflowY: 'initial',
          },
        }}
        {...buildDataTestIdAttribute(dataTestId)}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" padding="2.5px 12px 2.5px 24px">
          <Typography variant="body18Bold" color="grey.700">
            Add diagnoses
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              padding: '9px',
              path: {
                fill: '#4B5563',
              },
            }}
            {...buildDataTestIdAttribute(dataTestId, `button-close`)}
          >
            <CrossIcon />
          </IconButton>
        </Stack>
        <Divider />
        <ToggleTextButton
          firstTextButton={DIAGNOSES}
          secondTextButton={DIAGNOSIS_GROUPS}
          activeButton={activeButton}
          setActiveButton={setActiveButton}
          dataTestId={dataTestId}
        />
        {activeButton === DIAGNOSES ? (
          <DiagnosesTab handleClickScaleType={handleClickScaleType} dataTestId={dataTestId} />
        ) : (
          <DiagnosesGroupsTab
            handleClickScaleType={handleClickScaleType}
            dataTestId={buildDataTestId(dataTestId, 'giagnoses-group-tab')}
          />
        )}

        <ButtonGroup
          onApply={onApply}
          withoutApply={
            activeButton === DIAGNOSIS_GROUPS || (diagnosesGroupsInRule.length === 0 && diagnosesInRule.length === 0)
          }
          onCancel={onClose}
          cancelName="Cancel"
          sx={{ borderTop: border, padding: '24px', marginLeft: 0 }}
          dataTestId={dataTestId}
        />
      </Dialog>
    </PropsDiagnosesContext.Provider>
  );
};
